import { useCallback, useEffect, useState } from 'react';

/* ============================================================
   Tiny in-memory query cache with stale-while-revalidate.

   The admin pages each fetch their data in a useEffect on mount, so every
   navigation re-runs the query from scratch and shows a spinner — even when you
   were just looking at that data a second ago. This module keeps the last
   result for each key in memory (for the life of the tab) so a revisit paints
   instantly from cache while a fresh fetch revalidates in the background.

   Two ways to use it:
     • useCachedQuery(key, fetcher, deps)  — drop-in for a useEffect+useState
       load: returns { data, loading, error, refresh }, data is the cached value
       immediately when warm (loading stays false), then updated when the
       background fetch resolves.
     • readCache / writeCache / invalidate — for pages that manage their own
       state but want to seed it from cache and keep it warm.

   Call invalidate() (no args) on sign-out / restaurant switch so a different
   merchant never sees the previous one's cached rows.
   ============================================================ */

interface Entry<T = unknown> {
  data: T;
  ts: number;
}

const store = new Map<string, Entry>();

export function readCache<T>(key: string): T | undefined {
  return store.get(key)?.data as T | undefined;
}

export function writeCache<T>(key: string, data: T): void {
  store.set(key, { data, ts: Date.now() });
}

/** Drop cache entries. With no prefix, clears everything (use on sign-out). */
export function invalidate(prefix?: string): void {
  if (!prefix) {
    store.clear();
    return;
  }
  for (const k of Array.from(store.keys())) {
    if (k.startsWith(prefix)) store.delete(k);
  }
}

/**
 * Stale-while-revalidate data hook. Renders the cached value instantly (no
 * spinner) when warm, always kicks a background refresh, and exposes refresh()
 * for manual reloads (e.g. after a mutation). `key` must be stable for a given
 * resource (include the restaurant id and any filter that changes the result).
 */
export function useCachedQuery<T>(
  key: string,
  fetcher: () => Promise<T>,
  deps: readonly unknown[] = [],
): { data: T | undefined; loading: boolean; error: string | null; refresh: () => Promise<void> } {
  const [data, setData] = useState<T | undefined>(() => readCache<T>(key));
  const [loading, setLoading] = useState<boolean>(readCache<T>(key) === undefined);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    try {
      const fresh = await fetcher();
      writeCache(key, fresh);
      setData(fresh);
      setError(null);
    } catch (e) {
      // Keep showing the stale value on a background failure.
      if (readCache<T>(key) === undefined) {
        setError(e instanceof Error ? e.message : 'Failed to load.');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ...deps]);

  useEffect(() => {
    const cached = readCache<T>(key);
    if (cached !== undefined) {
      setData(cached);
      setLoading(false); // instant paint from cache
    } else {
      setLoading(true);
    }
    void run();
  }, [key, run]);

  return { data, loading, error, refresh: run };
}
